import { useEffect, useRef, useState } from "react";

/**
 * Keyboard navigation for dropdown / autocomplete lists.
 * Returns { activeIndex, setActiveIndex, onKeyDown, listRef } that you wire onto
 * the search input and the result list container. Handles ArrowUp / ArrowDown /
 * Enter / Escape / Home / End and keeps the active row scrolled into view.
 *
 * Usage:
 *   const { activeIndex, onKeyDown, listRef } = useKeyNav(items, (it) => choose(it), {
 *     onEscape: () => clearSearch(),
 *   });
 *   <input onKeyDown={onKeyDown} role="combobox" ... />
 *   <div ref={listRef} role="listbox">
 *     {items.map((it, i) => <row role="option" className={i === activeIndex ? "active" : ""} ...>)}
 *   </div>
 */
export function useKeyNav(items, onSelect, opts = {}) {
  const [activeIndex, setActiveIndex] = useState(-1);
  const listRef = useRef(null);

  // Reset/clamp when items change so the highlight stays valid.
  useEffect(() => {
    if (!items || items.length === 0) {
      setActiveIndex(-1);
    } else {
      setActiveIndex((i) => (i < 0 ? 0 : Math.min(i, items.length - 1)));
    }
  }, [items]);

  // Keep the highlighted option visible inside a scrollable dropdown.
  useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return;
    const options = listRef.current.querySelectorAll('[role="option"]');
    const el = options[activeIndex];
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex]);

  const onKeyDown = (e) => {
    if (!items || items.length === 0) {
      if (e.key === "Escape" && opts.onEscape) opts.onEscape(e);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % items.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + items.length) % items.length);
    } else if (e.key === "Enter") {
      if (activeIndex >= 0 && activeIndex < items.length) {
        e.preventDefault();
        onSelect(items[activeIndex]);
      }
    } else if (e.key === "Escape" && opts.onEscape) {
      e.preventDefault();
      opts.onEscape(e);
    } else if (e.key === "Home") {
      e.preventDefault();
      setActiveIndex(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setActiveIndex(items.length - 1);
    }
  };

  return { activeIndex, setActiveIndex, onKeyDown, listRef };
}
